<template>
  <div
    :class="{ 'notification-item': true, 'is-changing-state': markAsReadInProgress, 'is-not-readed': !notificationData.readed_at }"
    @click.stop="markAsReadNotification()"
    :style="serviceCss">

    <div
      @click="showReservation(notificationData.inquiry_id)"
      :data-is-reservation="!!notificationData.inquiry_id"
      :class="'notification-icon-wrapper notification-' + notificationData.type">
      <icon
        :name="'notification-' + notificationData.type"
        size="s" />
      <icon
        v-if="notificationData.inquiry_id"
        name="info-circle"
        size="s" />
    </div>

    <div class="notification-item-content">
      <strong>{{ notificationData.title }}</strong>
      <p v-pure-html="notificationData.message" @click="detectLinks"></p>
    </div>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';

export default {
  name: 'notifications-item',
  props: [
    'notificationData'
  ],
  computed: {
    serviceCss () {
      if (this.notificationData && this.notificationData.service && this.notificationData.service.color) {
        return 'border-left: 5px solid ' + this.notificationData.service.color;
      }

      return 'border-left: 5px solid transparent';
    }
  },
  data () {
    return {
      markAsReadInProgress: false
    };
  },
  methods: {
    markAsReadNotification () {
      if (this.notificationData.readed_at) {
        return;
      }

      this.markAsReadInProgress = true;

      this.$http.post('/panel/notifications/set-readed', {
        id: this.notificationData.id,
        type: 0
      }).then(response => {
        this.$store.commit('global/markAsReadNotification', this.notificationData.id);
        this.markAsReadInProgress = false;
        this.$bus.$emit('update-favicon');
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/notifications/set-readed', this.$router, this.$bus, this.markAsReadNotification.bind(this));
        this.markAsReadInProgress = false;
      });
    },
    showReservation (id) {
      if (!id) {
        return;
      }

      this.$bus.$emit('show-reservation-details-popup', id);
    },
    detectLinks (e) {
      if (e.target.tagName === 'A' && e.target.getAttribute('href').substr(0, 1) === '#') {
        e.preventDefault();
        let id = e.target.getAttribute('href').replace('#', '');
        id = parseInt(id, 10);
        this.$bus.$emit('show-reservation-details-popup', id);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.notification {
  &-item {
    align-items: center;
    border-bottom: 1px solid $color-border;
    display: flex;
    padding: 1.5rem 2rem;
    position: relative;

    &:after {
      background: darken($color-border, 5%);
      border: 2px solid darken($color-border, 5%);
      border-radius: 50%;
      content: "";
      height: 1.2rem;
      position: absolute;
      right: 1.5rem;
      width: 1.2rem;
    }

    &-content {
      text-align: left;
      width: calc(100% - 8rem);

      strong {
        color: $color-text-dark;
        display: block;
        font-size: 1.3rem;
        font-weight: 600;
        margin-bottom: .2rem;
        text-align: left;
      }

      p {
        color: $color-text-light;
        font-size: 1.3rem;
        margin: 0;
        padding: 0;
        text-align: left;
        width: 100%;
        word-break: keep-all;

        & /deep/ a {
          text-decoration: underline;

          &:active,
          &:focus,
          &:hover {
            color: $color-text-dark;
          }
        }
      }
    }

    &.is-not-readed {
      background: $color-bg-medium-alt;
      cursor: pointer;

      &:after {
        background: transparent;
      }

      .notification-icon-wrapper {
        &.notification-new-inquiry {
          background: $color-success;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-new-to-pay {
          background: $color-warning;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-accepted-inquiry {
          background: $color-success;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-cancelled-inquiry {
          background: $color-danger;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-inquiry-not-paid {
          background: $color-danger;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-change-termin,
        &.notification-reserve-list {
          background: $color-notice;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-inquiry-paid {
          background: $color-success;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-new-feature {
          background: $color-primary;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-system-break {
          background: $color-primary;

          & > svg {
            fill: $color-light;
          }
        }

        &.notification-warning {
          background: $color-danger;

          & > svg {
            fill: $color-light;
          }
        }
      }
    }

    &.is-changing-state {
      opacity: .5;
      pointer-events: none;
    }
  }

  &-icon {
    &-wrapper {
      align-items: center;
      border-radius: 50%;
      display: flex;
      height: 4rem;
      justify-content: center;
      margin-right: 2rem;
      width: 4rem;

      & > svg + svg {
        display: none;
      }

      &[data-is-reservation="true"]:hover > svg {
        display: none;

        & + svg {
          display: block;
        }
      }

      &.notification-new-inquiry {
        background: lighten($color-success, 52.5%);

        & > svg {
          fill: $color-success;
        }
      }

      &.notification-new-to-pay {
        background: lighten($color-warning, 52.5%);

        & > svg {
          fill: $color-warning;
        }
      }

      &.notification-accepted-inquiry {
        background: lighten($color-success, 52.5%);

        & > svg {
          fill: $color-success;
        }
      }

      &.notification-cancelled-inquiry {
        background: lighten($color-danger, 45%);

        & > svg {
          fill: $color-danger;
        }
      }

      &.notification-inquiry-not-paid {
        background: lighten($color-danger, 45%);

        & > svg {
          fill: $color-danger;
        }
      }

      &.notification-change-termin,
      &.notification-reserve-list {
        background: lighten($color-notice, 43.5%);

        & > svg {
          fill: $color-notice;
        }
      }

      &.notification-inquiry-paid {
        background: lighten($color-success, 52.5%);

        & > svg {
          fill: $color-success;
        }
      }

      &.notification-new-feature {
        background: lighten($color-primary, 60%);

        & > svg {
          fill: $color-primary;
        }
      }

      &.notification-system-break {
        background: lighten($color-primary, 57.5%);

        & > svg {
          fill: $color-primary;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/NotificationsItem.vue